import apiClient from "./ApiClient";
import { formatDateForApi } from "../utils/dateUtils";
import { getRestaurantId } from "../utils/JwtUtil";

/**
 * Fetch all categories.
 * @returns {Promise<Object[]>}
 */
export function fetchCategories() {
  return apiClient.get("/categories").then((res) => res.data);
}

/**
 * Fetch paginated meals for a restaurant.
 * @param {number|string} restaurantId
 * @param {number} page
 * @param {number} size
 * @returns {Promise<Object>} { content: Meal[], totalPages: number }
 */
export function fetchMealsByRestaurant(restaurantId, page, size) {
  return apiClient
    .get(`/meals/restaurant/${restaurantId}`, { params: { page, size } })
    .then((res) => res.data);
}

/**
 * Create a new meal.
 * @param {Object} payload
 * @returns {Promise<Object>} created meal
 */
export function createMeal(payload) {
  return apiClient.post("/meals/create", payload).then((res) => res.data);
}

/**
 * Update an existing meal.
 * @param {number|string} id
 * @param {Object} payload
 * @returns {Promise<Object>} updated meal
 */
export function updateMeal(id, payload) {
  return apiClient.put(`/meals/update/${id}`, payload).then((res) => res.data);
}

/**
 * Delete multiple meals.
 * @param {Array<number|string>} ids
 * @returns {Promise<void>}
 */
export function deleteMealsBatch(ids) {
  return apiClient
    .delete("/meals/batch/delete", { data: ids })
    .then((res) => res.data);
}

/**
 * Delete multiple meals.
 * @param {Array<number|string>} ids
 * @returns {Promise<void>}
 */
export function deleteMeal(id) {
  return apiClient
    .delete(`/meals/delete/${id}`)
    .then((res) => res.data);
}

/**
 * Get meal availability for a specific date range or meal.
 * @param {Object} params - Query parameters
 * @param {number|string} params.restaurantId - Restaurant ID
 * @param {string} [params.startDate] - Start date (YYYY-MM-DD)
 * @param {string} [params.endDate] - End date (YYYY-MM-DD)
 * @param {number|string} [params.mealId] - Specific meal ID
 * @returns {Promise<Object[]>} Array of MealAvailabilityResponse with {id, mealName, mealId, date}
 */
export function getMealAvailability(params) {
  const { restaurantId, startDate, endDate, mealId } = params;
  
  // If we have a date range, we need to make multiple calls for each date
  if (startDate && endDate && !mealId) {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const promises = [];
    
    // Helper function to format date in timezone-safe way
    const formatDateForApiLocal = formatDateForApi;
    
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const dateStr = formatDateForApiLocal(d);
      promises.push(
        apiClient
          .get(`/meals/availability/${restaurantId}/${dateStr}`)
          .then((res) => res.data)
          .catch(() => []) // Return empty array if no availability for this date
      );
    }
    
    return Promise.all(promises).then(results => results.flat());
  }
  
  // Single date query
  if (startDate && !endDate) {
    return apiClient
      .get(`/meals/availability/${restaurantId}/${startDate}`)
      .then((res) => res.data);
  }
  
  // Fallback for other cases
  return Promise.resolve([]);
}

/**
 * Set meal availability for specific date(s).
 * @param {Object[]} requests - Array of meal availability requests
 * @param {number|string} requests[].mealId - Meal ID
 * @param {string} requests[].date - Date (YYYY-MM-DD)
 * @returns {Promise<Object[]>} Created availability records
 */
export function setMealAvailability(requests) {
  // The API expects requests in the body, but restaurantId should be extracted from the first request
  // or we need to get it from somewhere else. Let's assume it's in the request objects.
  if (!Array.isArray(requests) || requests.length === 0) {
    return Promise.resolve([]);
  }
  
  // Get restaurantId from JWT or context (assuming it's available)
  // For now, we'll assume the restaurantId is passed or available globally
  const restaurantId = requests[0].restaurantId || getRestaurantId();
  
  // Filter out restaurantId from requests as the API takes it as path parameter
  const cleanedRequests = requests.map(({ restaurantId, ...rest }) => rest);
  
  return apiClient
    .post(`/meals/availability/${restaurantId}`, cleanedRequests)
    .then((res) => res.data);
}

/**
 * Remove meal availability by availability ID.
 * @param {number|string} availabilityId - The ID of the meal availability record to delete
 * @returns {Promise<void>}
 */
export function deleteMealAvailabilityById(availabilityId) {
  return apiClient
    .delete(`/meals/availability/${availabilityId}`)
    .then((res) => res.data);
}

/**
 * Remove meal availability for specific date(s).
 * @param {Object[]} requests - Array of meal availability requests to remove
 * @param {number|string} [requests[].id] - Availability ID (if known)
 * @param {number|string} [requests[].mealId] - Meal ID
 * @param {string} [requests[].date] - Date (YYYY-MM-DD)
 * @returns {Promise<void>}
 */
export function removeMealAvailability(requests) {
  if (!Array.isArray(requests) || requests.length === 0) {
    return Promise.resolve();
  }
  
  // If we have availability IDs, use the direct delete endpoint
  const deletePromises = requests
    .filter(request => request.id)
    .map(request => deleteMealAvailabilityById(request.id));
  
  if (deletePromises.length > 0) {
    return Promise.all(deletePromises);
  }
  
  // If no IDs available, we can't delete directly
  console.warn('removeMealAvailability: No availability IDs provided for deletion');
  return Promise.resolve();
}